import { createRouter, createWebHistory } from "vue-router";
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import { useAuthStore } from "@/stores/authStore";
import { rolesForPath } from "@/router/routeGuards";

// Import route components (these will be created later)
// import Dashboard from '@/views/Dashboard.vue'
// import Login from '@/views/auth/Login.vue'
// import Materials from '@/views/materials/Materials.vue'
// import Assignments from '@/views/assignments/Assignments.vue'
import ExamIndex from "@/modules/exams/pages/ExamIndex.vue";
// import Classes from '@/views/classes/Classes.vue'
// import Attendance from '@/views/attendance/Attendance.vue'
// import Reports from '@/views/reports/Reports.vue'
// import Forums from '@/views/forums/Forums.vue'

// Import actual Login and Register components
import Login from "@/pages/Login.vue";
import Register from "@/pages/Register.vue";

// Import actual Dashboard component
import Dashboard from "@/pages/Dashboard.vue";

// Import Profile component
import Profile from "@/modules/profile/pages/Profile.vue";

// Import Master Data components
import TeacherIndex from "@/modules/teachers/pages/TeacherIndex.vue";
import StudentIndex from "@/modules/students/pages/StudentIndex.vue";
import ClassIndex from "@/modules/classes/pages/ClassIndex.vue";
import SubjectIndex from "@/modules/subjects/pages/SubjectIndex.vue";
import MajorIndex from "@/modules/majors/pages/MajorIndex.vue";
import AcademicYearIndex from "@/modules/academic-years/pages/AcademicYearIndex.vue";
import SemesterIndex from "@/modules/semesters/pages/SemesterIndex.vue";
import UserIndex from "@/modules/users/pages/UserIndex.vue";
import RoleIndex from "@/modules/roles/pages/RoleIndex.vue";
import PermissionsIndex from "@/modules/permissions/pages/PermissionsIndex.vue";
import ClassSubjectIndex from "@/modules/classSubjects/pages/ClassSubjectIndex.vue";
import MaterialIndex from "@/modules/materials/pages/MaterialIndex.vue";
import StudentClassIndex from "@/modules/studentClasses/pages/StudentClassIndex.vue";
import QuestionBankIndex from "@/modules/question-banks/pages/QuestionBankIndex.vue";
import QuestionBankCreate from "@/modules/question-banks/pages/QuestionBankCreate.vue";
import QuestionBankEdit from "@/modules/question-banks/pages/QuestionBankEdit.vue";
import QuestionBankView from "@/modules/question-banks/pages/QuestionBankView.vue";
import AssignmentList from "@/modules/assignments/pages/AssignmentList.vue";
import StudentAssignments from "@/modules/assignments/pages/StudentAssignments.vue";
import AttendanceIndex from "@/modules/attendance/pages/AttendanceIndex.vue";
import AttendanceCreate from "@/modules/attendance/pages/AttendanceCreate.vue";
import AttendanceDetail from "@/modules/attendance/pages/AttendanceDetail.vue";
import AttendanceRecap from "@/modules/attendance/pages/AttendanceRecap.vue";
import StudentAttendance from "@/modules/attendance/pages/StudentAttendance.vue";
import ReportCardIndex from "@/modules/report-cards/pages/ReportCardIndex.vue";
import ReportCardShow from "@/modules/report-cards/pages/ReportCardShow.vue";
import StudentReports from "@/modules/report-cards/pages/StudentReports.vue";
import ForumIndex from "@/modules/forums/pages/ForumIndex.vue";
import ForumForm from "@/modules/forums/pages/ForumForm.vue";
import AiAssistant from "@/modules/ai/pages/AiAssistant.vue";
import AiProviders from "@/modules/ai/pages/AiProviders.vue";
import ForumShow from "@/modules/forums/pages/ForumShow.vue";
import ThreadShow from "@/modules/forums/pages/ThreadShow.vue";

// Create simple functional components to avoid runtime template compilation
const createPlaceholderComponent = (message: string) => ({
    setup() {
        return () => message;
    },
});

// Placeholder components for now
const NotFound = createPlaceholderComponent("<div>404 - Page Not Found</div>");

const routes = [
    {
        path: "/",
        redirect: "/dashboard",
    },
    {
        path: "/login",
        name: "Login",
        component: Login,
        meta: {
            layout: "auth",
            requiresAuth: false,
            title: "Login - LMS",
        },
    },
    {
        path: "/register",
        name: "Register",
        component: Register,
        meta: {
            layout: "auth",
            requiresAuth: false,
            title: "Register - LMS",
        },
    },
    {
        path: "/",
        component: DefaultLayout,
        meta: {
            requiresAuth: true,
        },
        children: [
            {
                path: "dashboard",
                name: "Dashboard",
                component: Dashboard,
                meta: {
                    title: "Dashboard - LMS",
                    icon: "HomeIcon",
                },
            },
            {
                path: "profile",
                name: "Profile",
                component: Profile,
                meta: {
                    title: "Profile - LMS",
                    icon: "UserIcon",
                },
            },
            {
                path: "users",
                name: "Users",
                component: UserIndex,
                meta: {
                    title: "Users - LMS",
                    icon: "UsersIcon",
                },
            },
            {
                path: "teachers",
                name: "Teachers",
                component: TeacherIndex,
                meta: {
                    title: "Teachers - LMS",
                    icon: "UserGroupIcon",
                },
            },
            {
                path: "students",
                name: "Students",
                component: StudentIndex,
                meta: {
                    title: "Students - LMS",
                    icon: "UserIcon",
                },
            },
            {
                path: "roles",
                name: "Roles",
                component: RoleIndex,
                meta: {
                    title: "Roles - LMS",
                    icon: "UsersIcon",
                },
            },
            {
                path: "permissions",
                name: "Permissions",
                component: PermissionsIndex,
                meta: {
                    title: "Permissions - LMS",
                    icon: "ShieldCheckIcon",
                },
            },
            {
                path: "classes",
                name: "Classes",
                component: ClassIndex,
                meta: {
                    title: "Classes - LMS",
                    icon: "AcademicCapIcon",
                },
            },
            {
                path: "subjects",
                name: "Subjects",
                component: SubjectIndex,
                meta: {
                    title: "Subjects - LMS",
                    icon: "BookOpenIcon",
                },
            },
            {
                path: "majors",
                name: "Majors",
                component: MajorIndex,
                meta: {
                    title: "Majors - LMS",
                    icon: "AcademicCapIcon",
                },
            },
            {
                path: "academic-years",
                name: "AcademicYears",
                component: AcademicYearIndex,
                meta: {
                    title: "Academic Years - LMS",
                    icon: "CalendarIcon",
                },
            },
            {
                path: "semesters",
                name: "Semesters",
                component: SemesterIndex,
                meta: {
                    title: "Semesters - LMS",
                    icon: "CalendarIcon",
                },
            },
            {
                path: "materials",
                name: "Materials",
                component: MaterialIndex,
                meta: {
                    title: "Materials - LMS",
                    icon: "DocumentTextIcon",
                    requiresRole: [
                        "teacher",
                        "administrator",
                        "super-administrator",
                    ],
                },
            },
            {
                path: "materials/create",
                name: "MaterialCreate",
                component: () =>
                    import("@/modules/materials/pages/MaterialCreate.vue"),
                meta: {
                    title: "Create Material - LMS",
                    requiresRole: [
                        "teacher",
                        "administrator",
                        "super-administrator",
                    ],
                },
            },
            {
                path: "materials/:id/edit",
                name: "MaterialEdit",
                component: () =>
                    import("@/modules/materials/pages/MaterialEdit.vue"),
                meta: {
                    title: "Edit Material - LMS",
                    requiresRole: [
                        "teacher",
                        "administrator",
                        "super-administrator",
                    ],
                },
            },
            {
                path: "student-classes",
                name: "Student Class Enrollment",
                component: StudentClassIndex,
                meta: {
                    title: "Student Class Enrollment - LMS",
                    icon: "AcademicCapIcon",
                    permission: "student-classes.view",
                },
            },
            {
                path: "assignments",
                name: "Assignments",
                component: AssignmentList,
                meta: {
                    title: "Assignments - LMS",
                    icon: "ClipboardDocumentListIcon",
                    requiresRole: [
                        "teacher",
                        "administrator",
                        "super-administrator",
                    ],
                },
            },
            {
                path: "assignments/create",
                name: "AssignmentCreate",
                component: () =>
                    import("@/modules/assignments/pages/AssignmentCreate.vue"),
                meta: {
                    title: "Create Assignment - LMS",
                    permission: "assignments.create",
                },
            },
            {
                path: "assignments/:id",
                name: "AssignmentDetail",
                component: () =>
                    import("@/modules/assignments/pages/AssignmentDetail.vue"),
                meta: {
                    title: "Assignment Details - LMS",
                    permission: "assignments.view",
                },
            },
            {
                path: "assignments/:id/edit",
                name: "AssignmentEdit",
                component: () =>
                    import("@/modules/assignments/pages/AssignmentEdit.vue"),
                meta: {
                    title: "Edit Assignment - LMS",
                    permission: "assignments.edit",
                },
            },
            {
                path: "assignments/:id/submissions",
                name: "AssignmentSubmissions",
                component: () =>
                    import("@/modules/assignments/pages/AssignmentSubmissions.vue"),
                meta: {
                    title: "Assignment Submissions - LMS",
                    permission: "assignments.view",
                },
            },
            {
                path: "assignment-grading/submissions/:submissionId",
                name: "SubmissionDetail",
                component: () =>
                    import("@/modules/assignments/pages/SubmissionDetail.vue"),
                meta: {
                    title: "Submission Details - LMS",
                    requiresRole: [
                        "teacher",
                        "administrator",
                        "super-administrator",
                    ],
                },
            },
            {
                path: "assignment-grading/submissions/:submissionId/grade",
                name: "SubmissionGrading",
                component: () =>
                    import("@/modules/assignments/pages/SubmissionDetail.vue"),
                meta: {
                    title: "Grade Submission - LMS",
                    requiresRole: [
                        "teacher",
                        "administrator",
                        "super-administrator",
                    ],
                },
            },
            {
                path: "question-banks",
                name: "QuestionBanks",
                component: QuestionBankIndex,
                meta: {
                    title: "Question Bank - LMS",
                    icon: "QuestionMarkCircleIcon",
                    requiresRole: [
                        "teacher",
                        "administrator",
                        "super-administrator",
                    ],
                },
            },
            {
                path: "question-banks/create",
                name: "QuestionBankCreate",
                component: QuestionBankCreate,
                meta: {
                    title: "Create Question - LMS",
                    permission: "question-banks.create",
                },
            },
            {
                path: "question-banks/:id",
                name: "QuestionBankView",
                component: QuestionBankView,
                meta: {
                    title: "Question Details - LMS",
                    permission: "question-banks.view",
                },
            },
            {
                path: "question-banks/:id/edit",
                name: "QuestionBankEdit",
                component: QuestionBankEdit,
                meta: {
                    title: "Edit Question - LMS",
                    permission: "question-banks.edit",
                },
            },
            {
                path: "exams",
                name: "Exams",
                component: ExamIndex,
                meta: {
                    title: "Exams - LMS",
                    icon: "QuestionMarkCircleIcon",
                    requiresRole: [
                        "teacher",
                        "administrator",
                        "super-administrator",
                    ],
                },
            },
            {
                path: "exams/create",
                name: "ExamCreate",
                component: () => import("@/modules/exams/pages/ExamCreate.vue"),
                meta: {
                    title: "Create Exam - LMS",
                    permission: "exams.create",
                },
            },
            {
                path: "exams/:id/edit",
                name: "ExamEdit",
                component: () => import("@/modules/exams/pages/ExamEdit.vue"),
                meta: {
                    title: "Edit Exam - LMS",
                    permission: "exams.edit",
                },
            },
            // Teacher Exam Results Routes
            {
                path: "exams/:examId/results",
                name: "teacher-exam-results",
                component: () =>
                    import("@/modules/exams/pages/TeacherResultsOverview.vue"),
                meta: {
                    title: "Exam Results - LMS",
                    requiresRole: [
                        "teacher",
                        "administrator",
                        "super-administrator",
                    ],
                },
            },
            {
                path: "exams/:examId/attempts/:attemptId/results",
                name: "teacher-exam-attempt-results",
                component: () =>
                    import("@/modules/exams/pages/StudentExamResults.vue"),
                meta: {
                    title: "Exam Attempt Results - LMS",
                    requiresRole: [
                        "teacher",
                        "administrator",
                        "super-administrator",
                    ],
                },
            },
            // Teacher Exam Grading Routes
            {
                path: "exams/:attemptId/grading",
                name: "teacher-exam-grading",
                component: () =>
                    import("@/modules/exams/pages/TeacherExamGrading.vue"),
                meta: {
                    title: "Exam Grading - LMS",
                    requiresRole: [
                        "teacher",
                        "administrator",
                        "super-administrator",
                    ],
                },
            },
            // Student Exam Routes
            {
                path: "student-exams",
                name: "StudentExams",
                component: () =>
                    import("@/modules/exams/pages/StudentExamList.vue"),
                meta: {
                    title: "My Exams - LMS",
                    icon: "QuestionMarkCircleIcon",
                    requiresRole: "student",
                },
            },
            {
                path: "student-exams/:examId/intro",
                name: "student-exam-intro",
                component: () =>
                    import("@/modules/exams/pages/StudentExamIntro.vue"),
                meta: {
                    title: "Exam Introduction - LMS",
                    requiresRole: "student",
                },
            },
            // Student Materials Routes
            {
                path: "student-materials",
                name: "StudentMaterials",
                component: () =>
                    import("@/modules/materials/pages/StudentMaterialsOverview.vue"),
                meta: {
                    title: "Materials - LMS",
                    icon: "DocumentTextIcon",
                    requiresRole: "student",
                },
            },
            // Student Assignments Routes
            {
                path: "student-assignments",
                name: "StudentAssignments",
                component: StudentAssignments,
                meta: {
                    title: "My Assignments - LMS",
                    icon: "ClipboardDocumentListIcon",
                    requiresRole: "student",
                },
            },
            {
                path: "student-assignments/:id",
                name: "StudentAssignmentDetail",
                component: () =>
                    import("@/modules/assignments/pages/StudentAssignmentDetail.vue"),
                meta: {
                    title: "Assignment Details - LMS",
                    requiresRole: "student",
                },
            },
            {
                path: "student-assignments/:id/submit",
                name: "StudentAssignmentSubmit",
                component: () =>
                    import("@/modules/assignments/pages/StudentAssignmentSubmit.vue"),
                meta: {
                    title: "Submit Assignment - LMS",
                    requiresRole: "student",
                },
            },
            {
                path: "student-exams/:examId/attempt/:attemptId",
                name: "student-exam-attempt",
                component: () =>
                    import("@/modules/exams/pages/StudentExamAttempt.vue"),
                meta: {
                    title: "Exam Attempt - LMS",
                    requiresRole: "student",
                },
            },
            {
                path: "student-exams/:examId/results",
                name: "student-exam-results-overview",
                component: () =>
                    import("@/modules/exams/pages/StudentResultsOverview.vue"),
                meta: {
                    title: "My Exam Results - LMS",
                    requiresRole: "student",
                },
            },
            {
                path: "student-exams/:examId/attempts/:attemptId/results",
                name: "student-exam-results",
                component: () =>
                    import("@/modules/exams/pages/StudentExamResults.vue"),
                meta: {
                    title: "Exam Attempt Results - LMS",
                    requiresRole: "student",
                },
            },
            {
                path: "student-exams/results/:attemptId",
                name: "student-exam-results-legacy",
                redirect: (to: any) => {
                    // Redirect legacy routes to new structure
                    return {
                        name: "student-exam-results",
                        params: {
                            examId: (to.query.examId as string) || "unknown",
                            attemptId: to.params.attemptId,
                        },
                    };
                },
            },
            {
                path: "class-subjects",
                name: "Class Subjects",
                component: ClassSubjectIndex,
                meta: {
                    title: "Class Subjects - LMS",
                    icon: "AcademicCapIcon",
                    permission: "class-subjects.view",
                },
            },
            {
                path: "attendance",
                name: "Attendance",
                component: AttendanceIndex,
                meta: {
                    title: "Attendance - LMS",
                    icon: "CalendarIcon",
                    permission: "attendance.view",
                },
            },
            {
                path: "attendance/create",
                name: "AttendanceCreate",
                component: AttendanceCreate,
                meta: {
                    title: "Create Attendance - LMS",
                    icon: "CalendarIcon",
                    permission: "attendance.create",
                },
            },
            {
                path: "attendance/recap",
                name: "AttendanceRecap",
                component: AttendanceRecap,
                meta: {
                    title: "Attendance Recap - LMS",
                    icon: "CalendarIcon",
                    permission: "attendance.view",
                },
            },
            {
                path: "attendance/:id",
                name: "AttendanceDetail",
                component: AttendanceDetail,
                meta: {
                    title: "Attendance Session - LMS",
                    icon: "CalendarIcon",
                    permission: "attendance.view",
                },
            },
            {
                path: "student-attendance",
                name: "StudentAttendance",
                component: StudentAttendance,
                meta: {
                    title: "My Attendance - LMS",
                    icon: "CalendarIcon",
                    permission: "attendance.view",
                },
            },
            {
                path: "reports",
                name: "Reports",
                component: ReportCardIndex,
                meta: {
                    title: "Reports - LMS",
                    icon: "ChartBarIcon",
                    permission: "reports.view",
                },
            },
            {
                path: "reports/:id",
                name: "ReportCardShow",
                component: ReportCardShow,
                meta: {
                    title: "Report Card - LMS",
                    icon: "ChartBarIcon",
                    permission: "reports.view",
                },
            },
            {
                path: "student-reports",
                name: "StudentReports",
                component: StudentReports,
                meta: {
                    title: "My Reports - LMS",
                    icon: "ChartBarIcon",
                    permission: "reports.view",
                },
            },
            {
                path: "student-reports/:id",
                name: "StudentReportShow",
                component: ReportCardShow,
                meta: {
                    title: "My Report - LMS",
                    icon: "ChartBarIcon",
                    permission: "reports.view",
                },
            },
            {
                path: "forums",
                name: "Forums",
                component: ForumIndex,
                meta: {
                    title: "Forums - LMS",
                    icon: "UserGroupIcon",
                    permission: "forums.view",
                },
            },
            {
                path: "forums/create",
                name: "ForumCreate",
                component: ForumForm,
                meta: {
                    title: "Create Forum - LMS",
                    icon: "UserGroupIcon",
                    permission: "forums.create",
                },
            },
            {
                path: "forums/:id",
                name: "ForumShow",
                component: ForumShow,
                meta: {
                    title: "Forum - LMS",
                    icon: "UserGroupIcon",
                    permission: "forums.view",
                },
            },
            {
                path: "forums/:id/edit",
                name: "ForumEdit",
                component: ForumForm,
                meta: {
                    title: "Edit Forum - LMS",
                    icon: "UserGroupIcon",
                    permission: "forums.view",
                },
            },
            {
                path: "forums/:id/threads/:threadId",
                name: "ThreadShow",
                component: ThreadShow,
                meta: {
                    title: "Thread - LMS",
                    icon: "UserGroupIcon",
                    permission: "forums.view",
                },
            },
            {
                path: "ai-assistant",
                name: "AiAssistant",
                component: AiAssistant,
                meta: {
                    title: "AI Assistant - LMS",
                    icon: "SparklesIcon",
                    permission: "ai-assistant.use",
                },
            },
            {
                path: "ai-providers",
                name: "AiProviders",
                component: AiProviders,
                meta: {
                    title: "AI Providers - LMS",
                    icon: "CpuChipIcon",
                    permission: "ai-providers.view",
                },
            },
        ],
    },
    {
        path: "/:pathMatch(.*)*",
        name: "NotFound",
        component: NotFound,
        meta: {
            title: "404 - Page Not Found",
        },
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        } else {
            return { top: 0 };
        }
    },
});

// Navigation guards
router.beforeEach((to, from, next) => {
    // Set page title
    if (to.meta.title) {
        document.title = to.meta.title as string;
    }

    // Check authentication
    const isAuthenticated = checkAuthentication(); // This would come from your auth store
    const requiresAuth = to.meta.requiresAuth !== false;

    if (requiresAuth && !isAuthenticated) {
        next({
            name: "Login",
            query: { redirect: to.fullPath },
        });
        return;
    }

    if (to.name === "Login" && isAuthenticated) {
        next({ name: "Dashboard" });
        return;
    }

    // Check permissions (if specified)
    if (to.meta.permission) {
        const hasPermission = checkPermission(to.meta.permission as string); // This would come from your auth store
        if (!hasPermission) {
            next({ name: "Dashboard" });
            return;
        }
    }

    // Check role requirements (if specified)
    if (to.meta.requiresRole) {
        const requiredRoles = Array.isArray(to.meta.requiresRole)
            ? (to.meta.requiresRole as string[])
            : [to.meta.requiresRole as string];
        const hasRequiredRole = requiredRoles.some((role) => checkRole(role));
        if (!hasRequiredRole) {
            next({ name: "Dashboard" });
            return;
        }
    }

    // Module-level role gate: send users away from modules their role never
    // sees in the sidebar instead of rendering a page that fails on API calls
    const allowedRoles = rolesForPath(to.path);
    if (allowedRoles && !allowedRoles.some((role) => checkRole(role))) {
        next({ name: "Dashboard" });
        return;
    }

    next();
});

router.afterEach((to, from) => {
    // You can add analytics or other post-navigation logic here
    console.log(`Navigated from ${from.path} to ${to.path}`);
});

// Helper functions using auth store
function checkAuthentication(): boolean {
    const authStore = useAuthStore();
    return authStore.isAuthenticated;
}

function checkPermission(permission: string): boolean {
    const authStore = useAuthStore();
    return authStore.hasPermission(permission);
}

function checkRole(role: string): boolean {
    const authStore = useAuthStore();
    return authStore.hasRole(role);
}

export default router;
